import React, { useCallback, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog } from 'components/modals';
import { useModalMinimize } from 'contexts/ModalMinimizeContext';
import type { TaxMasterSummary, TaxMasterVersion } from './taxMaster.types';
import './tax-master.css';

/**
 * ============================================================================
 * TAX MASTER DETAILS — the canonical view modal for one master (ADR-0019 §8)
 * ============================================================================
 * Fixed-text header, large container, `ViewField` blocks in a two-column grid,
 * status as badges, minimize-to-bar with record restore — the shape every
 * ViewModal in the template follows (workspace rule "ViewModal canonical").
 *
 * Below the fields: the version timeline. Deleting a scheduled version goes
 * through `ConfirmDialog`. Versions in force or superseded are read-only by
 * design: they priced documents, so they can only be superseded by a new
 * version, never edited.
 * ============================================================================
 */

export interface TaxMasterVersionsModalProps {
  isOpen: boolean;
  summary: TaxMasterSummary;
  versions: TaxMasterVersion[];
  busy?: boolean;
  canUpdate: boolean;
  canDelete: boolean;
  tabId?: string;
  moduleName?: string;
  onRestoreView?: (rateCode: string) => void;
  onAmend: (version: TaxMasterVersion) => void;
  onNewVersion: () => void;
  onDelete: (version: TaxMasterVersion) => void;
  onClose: () => void;
}

const ViewField: React.FC<{ label: string; value?: React.ReactNode; fullWidth?: boolean }> = ({ label, value, fullWidth }) => (
  <div className={fullWidth ? 'is-full' : undefined}>
    <label className="form-label">{label}</label>
    <div className="txm-field">
      {value === null || value === undefined || value === '' ? <span className="txm-dim">—</span> : value}
    </div>
  </div>
);

const StatusBadge: React.FC<{ v: TaxMasterVersion }> = ({ v }) => (
  <span className={`txm-badge ${v.isInForce ? 'txm-badge-inforce' : v.isFuture ? 'txm-badge-future' : 'txm-badge-past'}`}>
    {v.isInForce ? 'In force' : v.isFuture ? 'Scheduled' : 'Superseded'}
  </span>
);

const dateOnly = (v?: string | null) => (v ? v.slice(0, 10) : '');

export const TaxMasterVersionsModal: React.FC<TaxMasterVersionsModalProps> = ({
  isOpen, summary, versions, busy, canUpdate, canDelete, tabId, moduleName,
  onRestoreView, onAmend, onNewVersion, onDelete, onClose,
}) => {
  const { minimize } = useModalMinimize();
  const [pendingDelete, setPendingDelete] = useState<TaxMasterVersion | null>(null);

  const handleMinimize = useCallback(() => {
    if (!tabId || !moduleName) return;
    minimize({
      id: `${tabId}-view-modal-${summary.rateCode}`,
      title: `View - ${summary.rateCode}`,
      moduleName,
      tabId,
      onRestore: () => onRestoreView?.(summary.rateCode),
    });
    onClose();
  }, [tabId, moduleName, summary.rateCode, minimize, onClose, onRestoreView]);

  if (!isOpen) return null;

  const conditionKey = versions.find((v) => v.conditionKey)?.conditionKey ?? null;
  const inForce = versions.find((v) => v.isInForce) ?? null;

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-lg">
        <div className="modal-header">
          {/* Fixed text per the enterprise rule: the record code belongs in the body, not the title. */}
          <h2 className="modal-title">Tax Master Details</h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
            {tabId && (
              <button type="button" className="modal-header-btn" onClick={handleMinimize} title="Minimize to bar">
                <IconSystem.chevronDown size={16} />
              </button>
            )}
            <button type="button" className="modal-close" onClick={onClose} aria-label="Close">
              <IconSystem.x size={20} />
            </button>
          </div>
        </div>

        <div className="modal-content">
          <div className="txm-field-grid">
            <ViewField label="Code" value={<span className="txm-mono">{summary.rateCode}</span>} />
            <div>
              <label className="form-label">Type</label>
              <div style={{ paddingTop: '4px' }}>
                <span className={`txm-badge txm-badge-${summary.kind.toLowerCase()}`}>{summary.kind}</span>
                {summary.hasCess && <span className="txm-badge txm-badge-cess">+cess</span>}
              </div>
            </div>
            <ViewField label="Name" value={summary.name} fullWidth />
            <ViewField label="Category" value={summary.supplyCategory} />
            <ViewField label="Rate in force" value={<span className="txm-mono">{summary.rateSummary}</span>} />
            <ViewField label="In force from" value={dateOnly(inForce?.effectiveFrom ?? summary.inForceFrom)} />
            <ViewField label="Next change" value={summary.nextChangeOn ? <span className="txm-badge txm-badge-future">{dateOnly(summary.nextChangeOn)}</span> : null} />
            {conditionKey && <ViewField label="Evaluates against" value={<code>{conditionKey}</code>} fullWidth />}
          </div>

          <label className="form-label" style={{ marginTop: '1rem' }}>Versions</label>
          <table className="txm-versions">
            <thead>
              <tr>
                <th>Effective from</th>
                <th>Status</th>
                <th>{conditionKey ? 'Bands and rates' : 'Rates'}</th>
                {(canUpdate || canDelete) && <th className="is-actions">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {versions.map((v) => (
                <tr key={v.effectiveFrom}>
                  <td className="txm-mono">{dateOnly(v.effectiveFrom)}</td>
                  <td><StatusBadge v={v} /></td>
                  <td>
                    {v.bands.map((b, i) => (
                      <span key={i} className="txm-band-line">
                        {conditionKey && <span className="txm-dim txm-comp">{b.valueFrom ?? 0} – {b.valueTo ?? '∞'}</span>}
                        {b.components.map((c) => (
                          <span key={c.componentCode} className="txm-comp">
                            <span className="txm-mono">{c.componentCode}</span> {c.ratePercentage}%
                          </span>
                        ))}
                      </span>
                    ))}
                  </td>
                  {(canUpdate || canDelete) && (
                    <td className="is-actions">
                      {v.isFuture && canUpdate && (
                        <button type="button" className="btn-icon" disabled={busy} title="Amend scheduled version" onClick={() => onAmend(v)}>
                          <IconSystem.edit size={16} />
                        </button>
                      )}
                      {v.isFuture && canDelete && (
                        <button type="button" className="btn-icon" disabled={busy} title="Delete scheduled version" onClick={() => setPendingDelete(v)}>
                          <IconSystem.trash size={16} />
                        </button>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
          <div className="txm-note">
            Versions in force or superseded are immutable — documents were priced against them. A rate
            change goes in a new version with a future effective date.
          </div>
        </div>

        <div className="modal-footer">
          <button type="button" className="btn btn-secondary" onClick={onClose}>Close</button>
          {canUpdate && (
            <button type="button" className="btn btn-primary" disabled={busy} onClick={onNewVersion}>
              <IconSystem.add size={16} />
              New Version
            </button>
          )}
        </div>
      </div>

      <ConfirmDialog
        open={!!pendingDelete}
        title="Delete scheduled version?"
        message={
          `The version of ${summary.rateCode} scheduled for ${dateOnly(pendingDelete?.effectiveFrom)} has not taken effect yet, ` +
          'so it can be removed. The rate currently in force is unaffected.'
        }
        confirmLabel="Delete"
        cancelLabel="Cancel"
        variant="danger"
        loading={busy}
        loadingMessage="Deleting..."
        // Above the modal this dialog is rendered from.
        zIndex={10080}
        onConfirm={() => {
          const target = pendingDelete;
          setPendingDelete(null);
          if (target) onDelete(target);
        }}
        onCancel={() => setPendingDelete(null)}
      />
    </>
  );
};

export default TaxMasterVersionsModal;
